import { DateRangeFilter } from '../models/common.models';

/** Normalise API / form dates to yyyy-MM-dd for comparison. */
export function toDateKey(value: string | undefined | null): string {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isWithinDateRange(
  dateValue: string | undefined | null,
  range?: DateRangeFilter
): boolean {
  if (!range?.startDate || !range?.endDate) {
    return true;
  }

  const day = toDateKey(dateValue);
  if (!day) {
    return false;
  }

  return day >= range.startDate && day <= range.endDate;
}

export function filterByDateRange<T>(
  items: T[],
  range: DateRangeFilter | undefined,
  getDate: (item: T) => string | undefined | null
): T[] {
  if (!range?.startDate || !range?.endDate) {
    return items;
  }

  return items.filter((item) => isWithinDateRange(getDate(item), range));
}

/**
 * For point-in-time reports: filter when rows have dates; otherwise return full snapshot.
 */
export function filterSnapshotByDateRange<T>(
  items: T[],
  range: DateRangeFilter | undefined,
  getDate: (item: T) => string | undefined | null
): T[] {
  if (!range?.startDate || !range?.endDate) {
    return items;
  }

  const datedItems = items.filter((item) => toDateKey(getDate(item)));
  if (!datedItems.length) {
    return items;
  }

  return items.filter((item) => {
    const day = toDateKey(getDate(item));
    return !day || isWithinDateRange(day, range);
  });
}
